/**
 * 我的 Listing — 管理视图
 */

import { authFetch } from './api.js';
import { getCurrentPage, setCurrentPage, getCurrentSearch, setCurrentSearch } from './state.js';
import { showToast, formatDate, renderPagination } from './utils.js';
import { registerViewLoader } from './navigation.js';
import { openImageManager } from './image-manager.js';
import { openVideoManager } from './video-manager.js';
import { readScopedQuery, writeScopedQuery } from './url-state.js';
import { withTableLoading } from './view-state.js';

const LISTINGS_QUERY_SCHEMA = {
  page: { type: 'int', defaultValue: 1 },
  search: { type: 'string', defaultValue: '' },
};

let listingsViewMode = 'cards';
const skuSavingIds = new Set();

export function initListings() {
  hydrateListingsFiltersFromUrl();
  registerViewLoader('listings', () => loadListings());

  const searchBtn = document.getElementById('search-listings-btn');
  if (searchBtn) searchBtn.addEventListener('click', searchListings);

  document.querySelectorAll('#listings-view-toggle button').forEach((btn) => {
    btn.addEventListener('click', () => {
      listingsViewMode = btn.dataset.mode === 'table' ? 'table' : 'cards';
      syncListingsViewMode();
    });
  });

  syncListingsViewMode();
}

function hydrateListingsFiltersFromUrl() {
  const state = readScopedQuery('listings', LISTINGS_QUERY_SCHEMA);
  const searchInput = document.getElementById('search-listings');
  if (searchInput) searchInput.value = state.search || '';
}

export async function loadListings(page = null, search = null) {
  const queryState = readScopedQuery('listings', LISTINGS_QUERY_SCHEMA);
  const targetPage = page == null ? queryState.page : page;
  const targetSearch = search == null ? (queryState.search || '') : search;
  const searchInput = document.getElementById('search-listings');
  if (searchInput && search == null && searchInput.value.trim() !== targetSearch) {
    searchInput.value = targetSearch;
  }
  setCurrentPage(targetPage);
  setCurrentSearch(targetSearch);
  writeScopedQuery('listings', { page: targetPage, search: targetSearch });
  const tbody = document.getElementById('listings-tbody');

  const data = await withTableLoading({
    tbody,
    colSpan: 5,
    page: targetPage,
    errorMessage: '加载 Listing 列表失败',
    onRetry: (p) => loadListings(p, getCurrentSearch()),
    onError: (error) => {
      console.error('Error loading listings:', error);
      showToast('加载 Listing 列表失败', 'error');
    },
    task: async () => {
      const params = new URLSearchParams({ page: targetPage, size: 10, ...(targetSearch && { search: targetSearch }) });
    const response = await authFetch(`/api/saved-listings?${params}`);
    if (!response.ok) throw new Error('Failed to load listings');
      return response.json();
    },
  });
  if (!data) return;
  renderListingsAssets(data.listings);
  renderListingsTable(data.listings);
  renderPagination('listings-pagination', data.total, targetPage, 10, (p) => loadListings(p, getCurrentSearch()));
}

function syncListingsViewMode() {
  const assetGrid = document.getElementById('listings-asset-grid');
  const tableContainer = document.getElementById('listings-table-container');
  document.querySelectorAll('#listings-view-toggle button').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.mode === listingsViewMode);
  });
  if (assetGrid) assetGrid.hidden = listingsViewMode !== 'cards';
  if (tableContainer) tableContainer.hidden = listingsViewMode !== 'table';
}

function createListingElement(tagName, className = '', text = undefined) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = String(text);
  return element;
}

function createListingActionButton(action, listingId, text, className = 'btn btn-secondary btn-sm') {
  const button = createListingElement('button', className, text);
  button.type = 'button';
  button.dataset.action = action;
  button.dataset.listingId = String(listingId ?? '');
  return button;
}

function createListingEmptyState(message, colSpan = null) {
  const state = createListingElement('div', 'empty-state');
  state.append(
    createListingElement('div', 'empty-icon', '📄'),
    createListingElement('p', '', message),
  );
  if (colSpan == null) return state;

  const cell = document.createElement('td');
  cell.colSpan = colSpan;
  cell.appendChild(state);
  const row = document.createElement('tr');
  row.appendChild(cell);
  return row;
}

function createListingDetail(label, value) {
  const detail = createListingElement('div', 'listing-asset-detail');
  detail.append(
    createListingElement('strong', 'listing-asset-detail-label', `${label}：`),
    document.createTextNode(String(value)),
  );
  return detail;
}

function createListingAssetCard(listing) {
  const name = listing.name || listing.title?.substring(0, 30) || 'Untitled';
  const asin = listing.asin || 'N/A';
  const marketplace = listing.marketplace || '—';
  const title = listing.title || '';
  const titlePreview = title.length > 80 ? `${title.slice(0, 80)}…` : title;
  const updatedAt = formatDate(listing.updatedAt || listing.createdAt);
  const createdAt = formatDate(listing.createdAt);
  const sku = listing.sku || '';
  const skuSaving = skuSavingIds.has(String(listing.id));

  const card = createListingElement('div', 'stat-card listing-asset-card');
  const heading = createListingElement('div', 'listing-asset-heading');
  heading.append(
    createListingElement('div', 'stat-card-icon', '📦'),
    createListingElement('div', 'stat-card-label', 'Listing 资产'),
    createListingElement('div', 'stat-card-value listing-asset-name', name),
  );

  const meta = createListingElement('div', 'listing-asset-meta');
  meta.append(
    createListingDetail('ASIN', asin),
    createListingDetail('站点', marketplace),
    createListingDetail('更新时间', updatedAt),
    createListingDetail('创建时间', createdAt),
  );

  const skuField = createListingElement('label', 'listing-sku-field');
  skuField.appendChild(createListingElement('span', 'listing-sku-label', 'sku'));
  const skuInput = document.createElement('input');
  skuInput.type = 'text';
  skuInput.className = 'search-input listing-sku-input';
  skuInput.dataset.listingId = String(listing.id ?? '');
  skuInput.value = String(sku);
  skuInput.maxLength = 100;
  skuInput.autocomplete = 'off';
  skuInput.autocapitalize = 'off';
  skuInput.spellcheck = false;
  skuInput.disabled = skuSaving;
  skuField.appendChild(skuInput);

  const preview = createListingElement('div', 'listing-asset-preview', titlePreview || '暂无标题');
  const actions = createListingElement('div', 'listing-asset-actions');
  actions.append(
    createListingActionButton('view-images', listing.id, '查看图片'),
    createListingActionButton('view-videos', listing.id, '查看视频'),
    createListingActionButton('restore', listing.id, '恢复'),
    createListingActionButton('delete', listing.id, '删除', 'btn btn-danger btn-sm'),
  );
  card.append(heading, meta, skuField, preview, actions);
  return card;
}

function bindListingActions(root) {
  root.querySelectorAll('button[data-action]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const listingId = btn.dataset.listingId;
      const action = btn.dataset.action;
      if (action === 'view-images') viewListingImages(listingId);
      if (action === 'view-videos') viewListingVideos(listingId);
      if (action === 'restore') restoreListing(listingId);
      if (action === 'delete') deleteListing(listingId);
    });
  });
}

function renderListingsAssets(listings) {
  const grid = document.getElementById('listings-asset-grid');
  if (!grid) return;

  if (!listings || listings.length === 0) {
    const card = createListingElement('div', 'stat-card');
    card.appendChild(createListingElement('div', 'stat-card-label', '暂无保存的 Listing'));
    grid.replaceChildren(card);
    return;
  }

  grid.replaceChildren(...listings.map((listing) => createListingAssetCard(listing)));

  grid.querySelectorAll('.listing-sku-input').forEach((input) => bindSkuInput(input));
  bindListingActions(grid);
}

function bindSkuInput(input) {
  input.dataset.initialValue = input.value.trim();
  input.addEventListener('keydown', (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      input.blur();
    }
    if (event.key === 'Escape') {
      input.value = input.dataset.initialValue || '';
      input.blur();
    }
  });
  input.addEventListener('blur', () => saveListingSku(input));
}

async function saveListingSku(input) {
  const listingId = input.dataset.listingId;
  if (!listingId) return;
  const nextSku = input.value.trim();
  const prevSku = input.dataset.initialValue || '';
  if (nextSku === prevSku) return;

  skuSavingIds.add(String(listingId));
  input.disabled = true;
  try {
    const response = await authFetch(`/api/saved-listings/${listingId}`, {
      method: 'PUT',
      body: JSON.stringify({ sku: nextSku }),
    });
    if (!response.ok) throw new Error('Failed to update SKU');
    input.dataset.initialValue = nextSku;
    showToast('SKU 已更新', 'success');
  } catch (error) {
    console.error('Error updating SKU:', error);
    input.value = prevSku;
    showToast('SKU 更新失败', 'error');
  } finally {
    skuSavingIds.delete(String(listingId));
    input.disabled = false;
  }
}

function renderListingsTable(listings) {
  const tbody = document.getElementById('listings-tbody');
  if (!tbody) return;

  if (!listings || listings.length === 0) {
    tbody.replaceChildren(createListingEmptyState('暂无保存的 Listing', 5));
    return;
  }

  const rows = listings.map((listing) => {
    const name = listing.name || listing.title?.substring(0, 30) || 'Untitled';
    const asin = listing.asin || 'N/A';
    const marketplace = listing.marketplace || '—';
    const row = document.createElement('tr');

    const summary = document.createElement('td');
    summary.append(
      createListingElement('div', 'listing-table-name', name),
      createListingElement('div', 'listing-table-meta', `ASIN: ${asin} · 站点: ${marketplace}`),
    );
    row.appendChild(summary);

    const title = document.createElement('td');
    title.appendChild(createListingElement('div', 'listing-table-title', listing.title || ''));
    row.appendChild(title);
    row.appendChild(createListingElement('td', 'mono', formatDate(listing.createdAt)));
    row.appendChild(createListingElement('td', 'mono', formatDate(listing.updatedAt || listing.createdAt)));

    const actionCell = document.createElement('td');
    const actions = createListingElement('div', 'listing-table-actions');
    actions.append(
      createListingActionButton('view-images', listing.id, '查看图片'),
      createListingActionButton('view-videos', listing.id, '查看视频'),
      createListingActionButton('restore', listing.id, '恢复'),
      createListingActionButton('delete', listing.id, '删除', 'btn btn-danger btn-sm'),
    );
    actionCell.appendChild(actions);
    row.appendChild(actionCell);
    return row;
  });
  tbody.replaceChildren(...rows);
  bindListingActions(tbody);
}

function searchListings() {
  const search = document.getElementById('search-listings').value.trim();
  loadListings(1, search);
}

export function restoreListing(listingId) {
  window.location.href = `/listing.html?restoreId=${listingId}&step=3`;
}

export async function deleteListing(listingId) {
  if (!confirm('确定要删除这个 Listing 吗？')) return;

  try {
    const response = await authFetch(`/api/saved-listings/${listingId}`, { method: 'DELETE' });
    if (!response.ok) throw new Error('Failed to delete listing');

    showToast('Listing 已归档', 'success');
    await loadListings(getCurrentPage(), getCurrentSearch());
  } catch (error) {
    console.error('Error deleting listing:', error);
    showToast('删除失败', 'error');
  }
}

export async function viewListingImages(listingId) {
  openImageManager(listingId, 'listings');
}

export async function viewListingVideos(listingId) {
  openVideoManager(listingId, 'listings');
}
